import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import Chip from '../../../components/Chip';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import EmptyState from '../../../components/EmptyState';
import StatCard, { StatCardRow } from '../../../components/StatCard';
import {
  StandardTable,
  StandardTableBody,
  StandardTableCell,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableRow,
} from '../../../components/StandardTable';
import { DownloadIcon, ErrorIcon, ResetIcon, UploadIcon, WarningIcon } from '../../../components/icons';
import { HelpTrigger } from '../../../help';
import AdminModal from '../../components/AdminModal';
import OfficeTransitionsCard from './OfficeTransitionsCard';
import { formatDate } from '../../lib/format';

type ResultStatus = 'unofficial' | 'certified';
type Outcome = 'settle' | 'correct' | 'certify' | 'unchanged' | 'open';

interface ResultContest {
  contest_id: string;
  label: string;
  vote_for: number;
  result_status: ResultStatus | null;
  result_certified_at: string | null;
  winners: string[];
  candidate_count: number;
}

interface ResultBatch {
  id: string;
  file_name: string | null;
  result_status: ResultStatus;
  applied_at: string;
  undone_at: string | null;
  contest_count: number;
  uploaded_by_name: string | null;
}

interface PlannedContest {
  contest_id: string;
  label: string;
  outcome: Outcome;
  winners: { candidacy_id: string; name: string; re_elected: boolean }[];
  defeated: { candidacy_id: string; name: string }[];
  effective_date?: string;
  /** Workspaces whose tracking of the seat's incoming member a correction removes. */
  tracking_loss?: number;
  errors: string[];
  warnings: string[];
}

interface ResultsPlan {
  hash: string;
  result_status: ResultStatus;
  counts: { settle: number; correct: number; certify: number; unchanged: number; open: number; errors: number; warnings: number };
  file_errors: { line: number; message: string }[];
  contests: PlannedContest[];
}

const OUTCOME_LABEL: Record<Outcome, string> = {
  settle: 'Will settle',
  correct: 'Will correct',
  certify: 'Will certify',
  unchanged: 'No change',
  open: 'Left open',
};

const OUTCOME_TONE: Record<Outcome, 'positive' | 'caution' | 'info' | 'neutral'> = {
  settle: 'positive',
  correct: 'caution',
  certify: 'info',
  unchanged: 'neutral',
  open: 'neutral',
};

const labelClasses = 'mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary';

const apiError = (error: any, fallback: string) =>
  error?.response?.data?.error ?? error?.response?.data?.message ?? fallback;

const ResultChip: React.FC<{ status: ResultStatus | null }> = ({ status }) =>
  status === 'certified' ? <Chip label="Certified" tone="positive" size="sm" />
    : status === 'unofficial' ? <Chip label="Unofficial" tone="caution" size="sm" />
      : <Chip label="No result" tone="neutral" size="sm" />;

/**
 * Elections → cycle → Results: upload winners by contest
 * (Documentation/ELECTION_OUTCOMES_PRD.md §5.4–5.6). Preview first; apply settles
 * each contest with a winner, and the newest upload can be undone until its
 * Office-elects take office.
 */
const ElectionResultsTab: React.FC<{ calendarId: string; electionYear: number }> = ({ calendarId, electionYear }) => {
  const queryClient = useQueryClient();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [resultStatus, setResultStatus] = useState<ResultStatus>('unofficial');
  const [plan, setPlan] = useState<ResultsPlan | null>(null);
  const [undoTarget, setUndoTarget] = useState<ResultBatch | null>(null);
  const [confirmApply, setConfirmApply] = useState(false);

  const resultsKey = ['admin-elections', 'cycle-results', calendarId] as const;
  const results = useQuery({
    queryKey: resultsKey,
    queryFn: async () => {
      const response = await api.get(`/admin/elections/cycles/${calendarId}/results`);
      return response.data.data as { contests: ResultContest[]; batches: ResultBatch[] };
    },
  });

  const refresh = () => {
    queryClient.invalidateQueries(resultsKey);
    queryClient.invalidateQueries(['admin-elections', 'cycle-contests', calendarId]);
    queryClient.invalidateQueries(['admin-elections', 'transitions']);
  };

  const closeUpload = () => {
    setUploadOpen(false);
    setFile(null);
    setPlan(null);
    setResultStatus('unofficial');
  };

  const formFor = (extra: Record<string, string> = {}) => {
    const form = new FormData();
    if (file) form.append('file', file);
    form.append('result_status', resultStatus);
    Object.entries(extra).forEach(([key, value]) => form.append(key, value));
    return form;
  };

  const templateMutation = useMutation({
    mutationFn: async () => {
      const response = await api.get(`/admin/elections/cycles/${calendarId}/results/template`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `results-${electionYear}-general.csv`;
      link.click();
      URL.revokeObjectURL(url);
    },
    onError: (error) => toast.error(apiError(error, "Couldn't download the template")),
  });

  const previewMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/admin/elections/cycles/${calendarId}/results/preview`, formFor());
      return response.data.data as ResultsPlan;
    },
    onSuccess: setPlan,
    onError: (error) => toast.error(apiError(error, "Couldn't preview the results")),
  });

  const applyMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/admin/elections/cycles/${calendarId}/results/apply`, formFor({ hash: plan?.hash ?? '' }));
      return response.data.data as { counts: ResultsPlan['counts'] };
    },
    onSuccess: (data) => {
      const settled = data.counts.settle + data.counts.correct + data.counts.certify;
      toast.success(`Results applied to ${settled} contest${settled === 1 ? '' : 's'}`);
      closeUpload();
      refresh();
    },
    onError: (error: any) => {
      toast.error(apiError(error, "Couldn't apply the results"));
      if (error?.response?.status === 409) setPlan(null);
    },
  });

  const undoMutation = useMutation({
    mutationFn: (batchId: string) => api.post(`/admin/elections/results/batches/${batchId}/undo`),
    onSuccess: () => {
      toast.success('Upload undone');
      setUndoTarget(null);
      refresh();
    },
    onError: (error) => {
      toast.error(apiError(error, "Couldn't undo the upload"));
      setUndoTarget(null);
    },
  });

  const contests = results.data?.contests ?? [];
  const batches = results.data?.batches ?? [];
  const newestOpenBatch = batches.find((batch) => !batch.undone_at);
  const actionable = plan ? plan.counts.settle + plan.counts.correct + plan.counts.certify : 0;
  const canApply = Boolean(plan) && plan!.counts.errors === 0 && actionable > 0;
  const trackingLoss = (plan?.contests ?? []).reduce((sum, c) => sum + (c.tracking_loss ?? 0), 0);

  return (
    <div className="space-y-4">
      <div className="card p-5">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <span className="flex items-center gap-1.5">
            <h4 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary">General election results</h4>
            <HelpTrigger contentKey="election-results-upload" size="sm" ariaLabel="How the results upload works" />
          </span>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" icon={DownloadIcon}
              loading={templateMutation.isLoading} onClick={() => templateMutation.mutate()}>
              Download Template
            </Button>
            <Button variant="primary" size="sm" icon={UploadIcon} onClick={() => setUploadOpen(true)}>
              Upload Results
            </Button>
          </div>
        </div>

        <StatCardRow className="mb-4">
          <StatCard label="Contests" value={contests.length} loading={results.isLoading} />
          <StatCard label="Unofficial" variant="warning" loading={results.isLoading}
            value={contests.filter((c) => c.result_status === 'unofficial').length} />
          <StatCard label="Certified" variant="success" loading={results.isLoading}
            value={contests.filter((c) => c.result_status === 'certified').length} />
          <StatCard label="No result yet" loading={results.isLoading}
            value={contests.filter((c) => !c.result_status).length} />
        </StatCardRow>

        {results.isError ? (
          <EmptyState tone="error" size="compact" title="Couldn't load results" />
        ) : contests.length === 0 && !results.isLoading ? (
          <EmptyState size="compact" title="No general-election contests"
            hint="Add this cycle's general-election contests on the Overview tab before uploading results." />
        ) : (
          <StandardTable>
            <StandardTableHeader>
              <StandardTableRow>
                <StandardTableHeaderCell>Contest</StandardTableHeaderCell>
                <StandardTableHeaderCell>Winner</StandardTableHeaderCell>
                <StandardTableHeaderCell columnType="fit" responsiveHide="md">Candidates</StandardTableHeaderCell>
                <StandardTableHeaderCell columnType="fit">Result</StandardTableHeaderCell>
              </StandardTableRow>
            </StandardTableHeader>
            <StandardTableBody>
              {contests.map((contest) => (
                <StandardTableRow key={contest.contest_id}>
                  <StandardTableCell>{contest.label}</StandardTableCell>
                  <StandardTableCell>
                    {contest.winners.length > 0 ? contest.winners.join(', ') : (
                      <span className="text-text-tertiary dark:text-text-dark-tertiary">—</span>
                    )}
                  </StandardTableCell>
                  <StandardTableCell responsiveHide="md">{contest.candidate_count}</StandardTableCell>
                  <StandardTableCell><ResultChip status={contest.result_status} /></StandardTableCell>
                </StandardTableRow>
              ))}
            </StandardTableBody>
          </StandardTable>
        )}
      </div>

      {batches.length > 0 && (
        <div className="card p-5">
          <h4 className="mb-3 text-sm font-semibold text-text-primary dark:text-text-dark-primary">Uploads</h4>
          <StandardTable>
            <StandardTableHeader>
              <StandardTableRow>
                <StandardTableHeaderCell>Applied</StandardTableHeaderCell>
                <StandardTableHeaderCell>File</StandardTableHeaderCell>
                <StandardTableHeaderCell columnType="fit">Results</StandardTableHeaderCell>
                <StandardTableHeaderCell columnType="fit" responsiveHide="md">Contests</StandardTableHeaderCell>
                <StandardTableHeaderCell responsiveHide="md">By</StandardTableHeaderCell>
                <StandardTableHeaderCell columnType="fit"><span className="sr-only">Actions</span></StandardTableHeaderCell>
              </StandardTableRow>
            </StandardTableHeader>
            <StandardTableBody>
              {batches.map((batch) => (
                <StandardTableRow key={batch.id}>
                  <StandardTableCell>{formatDate(batch.applied_at)}</StandardTableCell>
                  <StandardTableCell>{batch.file_name ?? '—'}</StandardTableCell>
                  <StandardTableCell>
                    {batch.undone_at ? <Chip label="Undone" tone="neutral" size="sm" /> : <ResultChip status={batch.result_status} />}
                  </StandardTableCell>
                  <StandardTableCell responsiveHide="md">{batch.contest_count}</StandardTableCell>
                  <StandardTableCell responsiveHide="md">{batch.uploaded_by_name ?? '—'}</StandardTableCell>
                  <StandardTableCell>
                    {batch.id === newestOpenBatch?.id && (
                      <Button variant="ghost" size="sm" icon={ResetIcon} onClick={() => setUndoTarget(batch)}>
                        Undo
                      </Button>
                    )}
                  </StandardTableCell>
                </StandardTableRow>
              ))}
            </StandardTableBody>
          </StandardTable>
        </div>
      )}

      <OfficeTransitionsCard />

      <AdminModal
        isOpen={uploadOpen}
        title="Upload Results"
        subtitle={`${electionYear} general election`}
        size="xl"
        onClose={closeUpload}
        footer={<>
          <Button variant="secondary" size="sm" onClick={closeUpload}>Cancel</Button>
          <Button variant="outline" size="sm" disabled={!file} loading={previewMutation.isLoading}
            onClick={() => previewMutation.mutate()}>
            Preview
          </Button>
          <Button variant="primary" size="sm" disabled={!canApply} loading={applyMutation.isLoading}
            onClick={() => (trackingLoss > 0 ? setConfirmApply(true) : applyMutation.mutate())}>
            Apply to {actionable} contest{actionable === 1 ? '' : 's'}
          </Button>
        </>}
      >
        <div className="space-y-4">
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
            Fill in the <strong>winner</strong> column of the template. Every other active candidate in a contest with a
            winner becomes Defeated; contests with a blank winner are left as they are. Nothing changes until you apply.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="results-file" className={labelClasses}>Results file (.csv or .xlsx)</label>
              <input id="results-file" type="file" accept=".csv,.xlsx" className="input w-full"
                onChange={(event) => { setFile(event.target.files?.[0] ?? null); setPlan(null); }} />
            </div>
            <fieldset>
              <legend className={labelClasses}>These results are</legend>
              <div className="flex gap-4 pt-1.5 text-sm text-text-primary dark:text-text-dark-primary">
                {(['unofficial', 'certified'] as const).map((value) => (
                  <label key={value} className="flex items-center gap-1.5">
                    <input type="radio" name="result-status" value={value} checked={resultStatus === value}
                      onChange={() => { setResultStatus(value); setPlan(null); }} />
                    {value === 'unofficial' ? 'Unofficial' : 'Certified by the Secretary of State'}
                  </label>
                ))}
              </div>
            </fieldset>
          </div>

          {plan && (
            <>
              <StatCardRow minTileWidth="7rem">
                <StatCard label="Will settle" value={plan.counts.settle} variant="success" />
                <StatCard label="Corrections" value={plan.counts.correct} variant="warning" />
                <StatCard label="Certify only" value={plan.counts.certify} variant="info" />
                <StatCard label="Left open" value={plan.counts.open} />
                <StatCard label="Errors" value={plan.counts.errors} variant={plan.counts.errors ? 'danger' : 'default'} />
              </StatCardRow>

              {plan.file_errors.length > 0 && (
                <ul className="space-y-1 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700 dark:border-error-800 dark:bg-error-900/20 dark:text-error-300">
                  {plan.file_errors.map((error) => (
                    <li key={`${error.line}-${error.message}`}>Line {error.line}: {error.message}</li>
                  ))}
                </ul>
              )}

              <StandardTable>
                <StandardTableHeader>
                  <StandardTableRow>
                    <StandardTableHeaderCell>Contest</StandardTableHeaderCell>
                    <StandardTableHeaderCell columnType="fit">Outcome</StandardTableHeaderCell>
                    <StandardTableHeaderCell>Winner</StandardTableHeaderCell>
                    <StandardTableHeaderCell columnType="fit" responsiveHide="md">Defeated</StandardTableHeaderCell>
                    <StandardTableHeaderCell columnType="fit" responsiveHide="md">Takes office</StandardTableHeaderCell>
                  </StandardTableRow>
                </StandardTableHeader>
                <StandardTableBody>
                  {plan.contests
                    .filter((contest) => contest.outcome !== 'open' || contest.errors.length > 0)
                    .map((contest) => (
                      <StandardTableRow key={contest.contest_id}>
                        <StandardTableCell>
                          {contest.label}
                          {[...contest.errors.map((m) => ({ m, error: true })), ...contest.warnings.map((m) => ({ m, error: false }))]
                            .map(({ m, error }) => {
                              const Icon = error ? ErrorIcon : WarningIcon;
                              return (
                                <p key={m} className={`mt-1 flex items-start gap-1 text-xs ${error
                                  ? 'text-error-600 dark:text-error-400' : 'text-warning-700 dark:text-warning-400'}`}>
                                  <Icon className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
                                  {m}
                                </p>
                              );
                            })}
                        </StandardTableCell>
                        <StandardTableCell>
                          <Chip label={contest.errors.length ? 'Error' : OUTCOME_LABEL[contest.outcome]}
                            tone={contest.errors.length ? 'negative' : OUTCOME_TONE[contest.outcome]} size="sm" />
                        </StandardTableCell>
                        <StandardTableCell>
                          {contest.winners.map((w) => `${w.name}${w.re_elected ? ' (re-elected)' : ''}`).join(', ') || '—'}
                        </StandardTableCell>
                        <StandardTableCell responsiveHide="md">{contest.defeated.length}</StandardTableCell>
                        <StandardTableCell responsiveHide="md">
                          {contest.effective_date ? formatDate(contest.effective_date) : '—'}
                        </StandardTableCell>
                      </StandardTableRow>
                    ))}
                </StandardTableBody>
              </StandardTable>
              {plan.counts.open > 0 && (
                <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                  {plan.counts.open} contest{plan.counts.open === 1 ? ' has' : 's have'} no winner in the file and {plan.counts.open === 1 ? 'is' : 'are'} left as {plan.counts.open === 1 ? 'it is' : 'they are'}.
                </p>
              )}
            </>
          )}
        </div>
      </AdminModal>

      <ConfirmationDialog
        isOpen={confirmApply}
        title="Remove workspace tracking?"
        message={`Correcting these results replaces the incoming member for ${trackingLoss === 1 ? 'a seat that 1 workspace tracks' : `seats tracked by ${trackingLoss} workspaces`}. That tracking, and any predictions and notes on it, is removed.`}
        confirmText="Apply Anyway"
        variant="danger"
        onConfirm={() => { setConfirmApply(false); applyMutation.mutate(); }}
        onCancel={() => setConfirmApply(false)}
      />

      <ConfirmationDialog
        isOpen={undoTarget !== null}
        title="Undo this upload?"
        message={`Every candidacy and contest it changed goes back to how it was, and its Office-elect roles are removed. ${undoTarget?.file_name ?? ''}`.trim()}
        confirmText="Undo Upload"
        variant="warning"
        onConfirm={() => undoTarget && undoMutation.mutate(undoTarget.id)}
        onCancel={() => setUndoTarget(null)}
      />
    </div>
  );
};

export default ElectionResultsTab;
